/**
 * Utility for fetching verse text from prebuilt HTML files.
 */

import type { BibleLocation } from '@skjv/common';
import { getBookInfo, formatShortReference } from '@skjv/common';
import { publicPath } from '@utils/public-path';

// Cache fetched chapters to avoid repeated requests
const chapterCache: Map<string, Document> = new Map();

/**
 * Get the HTML file path for a chapter.
 */
function getChapterPath(location: BibleLocation): string {
  const book = getBookInfo(location.testamentIndex, location.bookIndex);
  if (!book) throw new Error('Unknown book');
  // Build stamp busts browser HTTP caches — filenames never change.
  return publicPath(`html/${book.fileName}/${location.chapterIndex}.html?v=${__BUILD_ID__}`);
}

/**
 * Fetch and parse a chapter HTML document.
 */
async function fetchChapter(location: BibleLocation): Promise<Document> {
  const path = getChapterPath(location);

  // Check cache
  if (chapterCache.has(path)) {
    return chapterCache.get(path)!;
  }

  const response = await fetch(path);
  if (!response.ok) {
    throw new Error(`Failed to fetch chapter: ${path}`);
  }

  const html = await response.text();
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Cache the result
  chapterCache.set(path, doc);

  return doc;
}

/**
 * Extract text from a verse element, including punctuation.
 */
function extractVerseText(verseElement: Element): string {
  let text = '';

  // Walk through all child nodes to capture text and punctuation
  verseElement.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      // Text nodes may contain punctuation between words
      const nodeText = node.textContent || '';
      text += nodeText;
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as Element;
      // Skip verse numbers
      if (el.classList.contains('verse-num')) {
        return;
      }
      // Get text from word elements (strongs-word, word, etc.)
      const nodeText = el.textContent || '';
      text += nodeText;
    }
  });

  // Clean up whitespace while preserving punctuation
  return text.replace(/\s+/g, ' ').trim();
}

/**
 * Get the text for a single verse or range of verses.
 */
export async function getVerseText(
  startingLocation: BibleLocation,
  endingLocation?: BibleLocation,
): Promise<string> {
  const doc = await fetchChapter(startingLocation);

  const startVerse = startingLocation.verseIndex;
  const endVerse = endingLocation ? endingLocation.verseIndex : startVerse;

  const verses: string[] = [];

  for (let i = startVerse; i <= endVerse; i++) {
    // Verse IDs are formatted as v-{testament}-{book}-{chapter}-{verse}
    const verseId = `v-${startingLocation.testamentIndex}-${startingLocation.bookIndex}-${startingLocation.chapterIndex}-${i}`;
    const verseElement = doc.getElementById(verseId);

    if (verseElement) {
      verses.push(extractVerseText(verseElement));
    }
  }

  return verses.join(' ');
}

/**
 * Get a formatted verse title (e.g., "Jhn 1:4").
 */
export function getVerseTitle(location: BibleLocation): string {
  return formatShortReference(location);
}

/**
 * Get a formatted verse range title (e.g., "Jhn 1:1-5").
 */
export function getVerseRangeTitle(
  startingLocation: BibleLocation,
  endingLocation?: BibleLocation,
): string {
  const startRef = formatShortReference(startingLocation);
  if (!endingLocation) return startRef;

  // If same chapter, just show "Jhn 1:1-5"
  if (
    startingLocation.testamentIndex === endingLocation.testamentIndex &&
    startingLocation.bookIndex === endingLocation.bookIndex &&
    startingLocation.chapterIndex === endingLocation.chapterIndex
  ) {
    return `${startRef}-${endingLocation.verseIndex + 1}`;
  }

  // Different chapters: "Jhn 1:1 - Jhn 2:3"
  return `${startRef} - ${formatShortReference(endingLocation)}`;
}

/** Browser-side VerseTextSource — fetches prebuilt chapter HTML via the public path. */
export const browserVerseTextSource = {
  getVerseText,
  getVerseRangeTitle,
};
